import { default as injectSource, injectCollection } from './inject';
import { uaIsIE11, uaIsEdge12 } from './ua-sniffing';

/**
 * Design System JS launcher.
 *
 * @module 00-utilities/app-loader
 * @version 1.0.0
 *
 * @author <Sergio Jiménez Herena> [sergio.jimenez@selectra.info]
 */


/**
 * This is a multi purpose function to set up the environment before launching
 *   our application code.
 *   It will perform the following tasks:
 *     1. Test browser features.
 *     2. Load a polyfill bundle in case it's needed by our application code.
 *     3. Inject an arbitrary collection of CSS/JS resources to the DOM.
 *     4. Callback app code once the mentioned processes have executed.
 *   *** IMPORTANT
 *   *** Keep this function IE11 compatible.
 *   *** Syntax will be transpiled, but it's executed before the polyfills
 *   *** are loaded!
 *
 * @function
 * @param {object} init
 *   Initialization object
 * @param {Function} init.appCode - Application code callback.
 * @param {*[]}  [init.appArgs=[]]
 *   Callback arguments.
 * @param {Array[]} [init.appRequiredFeatures]
 *     Array of features present on the application code.
 * @param {object} [init.polyfillUrls]
 *     An object containing the paths for the polyfill bundles.
 * @param {string} [init.executeOn='DOMContentLoaded']
 *     The event to which the callback will be listening. One of
 *     'DOMContentLoaded' or 'load'.
 * @param {Array} [init.injections=[]]
 *     Paths of resources to inject in the DOM.
 */
export default function appLoader( init = {}) {

  if ( !init.appCode || typeof init.appCode !== 'function' ) {
    throw new TypeError( '\'init.appCode\' must be a function reference' );
  }

  const

    // Set default initialization options
    {
      appCode,
      appArgs,
      appRequiredFeatures,
      polyfillUrls,
      executeOn,
      injections
    } =
      {
        appCode: null,
        appArgs: [],
        appRequiredFeatures: [],
        polyfillUrls: {
          full: '/js/polyfill/polyfill-full.min.js',
          partial: '/js/polyfill/polyfill-partial.min.js',
          svg4everybody: '/js/polyfill/svg4everybody.min.js',
        },
        executeOn: 'DOMContentLoaded',
        injections: [],
        ...init,
      },

    launchApplication =
      injections.length ?
        () => {
          injectCollection( injections, processAppCode );
        } :
        processAppCode;

  let polyfillBundle = null;


  if ( !window.selectra ) {
    window.selectra = {};
  }

  if ( !window.selectra.ds ) {
    window.selectra.ds = {
      polyfillBundle: null,
      isReady: false,
    };
    checkFeatures();
  }

  // Check if it's a browser without support for SVG External Contexts
  if (
    ( uaIsIE11() || uaIsEdge12()) &&
    (
      undefined !== window.svg4everybody &&
      window.svg4everybody !== 'function'
    )
  ) {
    injectSource( polyfillUrls.svg4everybody, () => window.svg4everybody());
  }
  // Inject appropriate polyfill bundle based on feature detection checks.
  if (
    'partial' === polyfillBundle ||
    'full' === polyfillBundle
  ) {
    injectSource( polyfillUrls[ polyfillBundle ], launchApplication );
  } else {
    launchApplication();
  }


  /**
   * Check browser features and initialize 'window.selectra.ds'
   *
   * @function
   */
  function checkFeatures() {

    const features = {
      // Number
      number: {
        isInteger: {
          isSupported: 'isInteger' in Number,
          flag: 1,
        },
        parseInt: {
          isSupported: 'parseInt' in Number,
          flag: 1 << 1,
        },
        parseFloat: {
          isSupported: 'parseFloat' in Number,
          flag: 1 << 2,
        }
      },
      // Array
      array: {
        from: {
          isSupported: 'from' in Array,
          flag: 1 << 3,
        },
        forEach: {
          isSupported: 'forEach' in Array.prototype,
          flag: 1 << 4,
        },
        includes: {
          isSupported: 'includes' in Array.prototype,
          flag: 1 << 5,
        },
        slice: {
          isSupported: 'slice' in Array.prototype,
          flag: 1 << 6,
        }
      },
      // Math
      math: {
        sign: {
          isSupported: 'sign' in Math,
          flag: 1 << 7,
        },
      },
      // String
      string: {
        fromCodePoint: {
          isSupported: 'fromCodePoint' in String,
          flag: 1 << 8,
        },
        codePointAt: {
          isSupported: 'codePointAt' in String.prototype,
          flag: 1 << 9,
        },
        includes: {
          isSupported: 'includes' in String.prototype,
          flag: 1 << 10,
        },
        repeat: {
          isSupported: 'repeat' in String.prototype,
          flag: 1 << 11,
        },
      },
      // Object
      object: {
        assign: {
          isSupported: 'assign' in Object,
          flag: 1 << 12,
        }
      },
      // Symbol
      symbol: {
        all: {
          isSupported: 'Symbol' in window,
          flag: 1 << 13,
        }
      },
      // Promise
      promise: {
        all: {
          isSupported: typeof Promise === 'function',
          flag: 1 << 14,
        },
      },
      // ParentNode
      parentNode: {
        append: {
          isSupported:
            'append' in Element.prototype &&
            'append' in Document.prototype &&
            'append' in DocumentFragment.prototype,
          flag: 1 << 15,
        },
      },
      // Element
      element: {
        closest: {
          isSupported: 'closest' in Element.prototype,
          flag: 1 << 16,
        },
        matches: {
          isSupported: 'matches' in Element.prototype,
          flag: 1 << 17,
        },
        classList: {
          isSupported: 'classList' in Element.prototype,
          flag: 1 << 18,
        },
      },
      // NodeList
      nodeList: {
        forEach: {
          isSupported: 'forEach' in NodeList.prototype,
          flag: 1 << 19,
        },
      },
      // CustomEvent
      customEvent: {
        all: {
          isSupported: 'CustomEvent' in window,
          flag: 1 << 20,
        },
      },
      // Scroll API
      scroll: {
        all: {
          isSupported: 'scroll' in window,
          flag: 1 << 21,
        },
      },
      // IntersectionObserver API
      intersectionObserver: {
        all: {
          isSupported:
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'intersectionRatio' in window.IntersectionObserverEntry.prototype,
          flag: 1 << 22,
        },
      },
      // Fetch
      fetch: {
        all: {
          isSupported: 'fetch' in window,
          flag: 1 << 23,
        },
      },
      // Position: sticky
      positionSticky: {
        all: {
          isSupported: 'scrollBehavior' in document.documentElement.style,
          flag: 1 << 24,
        },
      },
    };

    // Populate feature flag-sets
    let unsupportedFeats =
      ( function getUnsupportedFeatures() {
        const hasOwnProperty = Object.prototype.hasOwnProperty;
        let unsupported = [];
        for ( const featureName in features ) {
          if ( hasOwnProperty.call( features, featureName )) {
            const feature = features[ featureName ];
            for ( const propertyName in feature ) {
              if ( hasOwnProperty.call( feature, propertyName )) {
                const
                  property = feature[ propertyName ],
                  propertyFlag = property.flag;
                if ( !property.isSupported ) {
                  unsupported.push( propertyFlag );
                }
              }
            }
          }
        }
        return unsupported;
      }());

    // eslint-disable-next-line one-var
    const

      // The flags for features included in the partial polyfill bundle.
      partialFeaturesFlags = [
        features.scroll.all.flag,
        features.intersectionObserver.all.flag,
        features.fetch.all.flag,
        features.positionSticky.all.flag
      ];


    // Ditch the unsupported features *not required* by application code.
    if ( unsupportedFeats.length && appRequiredFeatures.length ) {

      let requiredFeaturesMask = 0;

      appRequiredFeatures.forEach( requiredFeature => {
        const
          feature = requiredFeature[ 0 ],
          subFeatures = requiredFeature[ 1 ];
        subFeatures.forEach( subFeature => {
          requiredFeaturesMask |= features[ feature ][ subFeature ].flag;
        });
      });

      unsupportedFeats =
        unsupportedFeats.filter( featureFlag => {
          return (
            ( featureFlag & requiredFeaturesMask ) === featureFlag
          );
        });
    }

    if ( unsupportedFeats.length === 0 ) {
      console.log( 'No polyfills needed.' );
      window.selectra.ds.polyfillBundle = polyfillBundle = 'none';
    } else if ( checkUnsupportedFeaturesInBundle( partialFeaturesFlags )) {
      console.log( 'Loading partial polyfill bundle.' );
      window.selectra.ds.polyfillBundle = polyfillBundle = 'partial';
    } else {
      console.log( 'Loading full polyfill bundle.' );
      window.selectra.ds.polyfillBundle = polyfillBundle = 'full';
    }


    /**
     * Checks if the unsupported features are polyfilled by a
     *   polyfill bundle.
     *
     * @function
     * @param {Array} polyBundleFlags
     *   Array of unsupported features flags.
     * @returns {boolean}
     *   True if all required features are present in the  polyfill bundle,
     *   false otherwise.
     */
    function checkUnsupportedFeaturesInBundle( polyBundleFlags ) {
      const
        totalUnsupportedFeatures = unsupportedFeats.length,
        polyBundleMask =
          polyBundleFlags.reduce(( accumulator, flag ) => {
            return accumulator | flag;
          });
      let allFeaturesInBundle = true;
      for ( let feature = 0; feature < totalUnsupportedFeatures; ++feature ) {
        const featureFlag = unsupportedFeats[ feature ];
        if (( featureFlag & polyBundleMask ) !== featureFlag ) {
          allFeaturesInBundle = false;
          break;
        }
      }
      return allFeaturesInBundle;
    }
  }


  /**
   * Sets application code function to be called back on the 'DOMContentLoaded'
   *   event. In the case it has already been fired, executes right ahead.
   *
   * @function
   */
  function processAppCode() {
    const
      appCodeWrapper = () => {
        document.dispatchEvent( new Event( 'DSReady' ));
        window.selectra.ds.isReady = true;
        appCode.apply( null, appArgs );
      },

      executionCallback = event => {
        appCodeWrapper();
        event.currentTarget
          .removeEventListener( event.type, executionCallback );
      };

    switch ( executeOn ) {
    default:
    case 'DOMContentLoaded':
      document.readyState === 'loading' ?
        window.addEventListener( 'DOMContentLoaded', executionCallback ) :
        appCodeWrapper();
      break;
    case 'load':
      document.readyState !== 'complete' ?
        window.addEventListener( 'load', executionCallback ) :
        appCodeWrapper();
      break;
    }

  }

}
